<template>
	<div id="permissionConfig">
		<page-header title="权限配置">
			<template #actions>
				<el-button type="primary" size="small" icon="el-icon-refresh" :loading="loading"
					@click="loadMatrix">刷新</el-button>
			</template>
		</page-header>

		<!-- Tab 切换 -->
		<div class="config-card" v-loading="loading">
			<el-tabs v-model="activeTab" type="card">
				<!-- ========== 权限分配 ========== -->
				<el-tab-pane label="功能权限" name="permissions">
					<div class="tab-desc">
						<i class="el-icon-key"></i>
						按菜单分组配置 — 关闭"查看"主开关会自动禁用该菜单下所有子权限
					</div>
					<!-- 顶部: 角色选择 -->
					<div class="perm-toolbar">
						<span class="tb-label">选择角色:</span>
						<el-radio-group v-model="activeRole" size="small">
							<el-radio-button v-for="(roleName, roleCode) in roles" :key="roleCode"
								:label="roleCode" :disabled="roleCode === 'ADMIN'">
								{{ roleName }}
							</el-radio-button>
						</el-radio-group>
						<el-tag v-if="activeRole === 'ADMIN'" type="success" size="small" style="margin-left:12px">
							<i class="el-icon-check"></i> 管理员拥有全部权限,不可修改
						</el-tag>
					</div>

					<!-- 提示: 当前角色启用了哪些菜单 -->
					<el-alert v-if="activeRole && activeRole !== 'ADMIN' && noMenusForRole"
						type="info" :closable="false" show-icon style="margin-bottom:12px"
						title="该角色暂未配置任何菜单"
						description="请先去『菜单配置』为该角色开启菜单, 对应的功能权限会在此显示" />

					<!-- 按菜单分组的折叠面板 -->
					<el-collapse v-model="expandedGroups" v-if="activeRole && activeRole !== 'ADMIN'">
						<el-collapse-item v-for="group in permGroups" :key="group.key" :name="group.key">
							<template slot="title">
								<div class="group-header">
									<i :class="group.icon" />
									<span class="group-title">{{ group.label }}</span>
									<span class="group-count">
										{{ countEnabled(group) }} / {{ group.permissions.length }}
									</span>
									<el-tag v-if="group.viewPerm && !isPermChecked(activeRole, group.viewPerm)"
										type="info" size="mini" style="margin-left:8px">已关闭</el-tag>
								</div>
							</template>
							<div class="group-body">
								<div v-for="permCode in group.permissions" :key="permCode" class="perm-item">
									<div class="perm-meta">
										<span class="item-label">
											{{ permissions[permCode] || permCode }}
											<el-tag v-if="permCode === group.viewPerm" size="mini" type="warning" style="margin-left:6px">主开关</el-tag>
										</span>
										<span class="item-code">{{ permCode }}</span>
									</div>
									<el-switch
										:value="isPermChecked(activeRole, permCode)"
										@change="togglePermSmart(group, permCode, $event)"
										:disabled="saving || (group.viewPerm && permCode !== group.viewPerm && !isPermChecked(activeRole, group.viewPerm))"
										active-color="#667eea" inactive-color="#dcdfe6" />
								</div>
							</div>
						</el-collapse-item>
					</el-collapse>
				</el-tab-pane>

				<!-- ========== 菜单分配 ========== -->
				<el-tab-pane label="菜单配置" name="menus">
					<div class="tab-desc">
						<i class="el-icon-menu"></i>
						控制各角色可见的侧边栏菜单项，修改后用户需重新登录
					</div>
					<el-table :data="menuRows" border stripe
						:header-cell-style="headerStyle">
						<el-table-column label="菜单" width="200" fixed>
							<template slot-scope="{ row }">
								<div class="item-cell">
									<i :class="row.icon" style="margin-right:6px; color:#667eea;"></i>
									<span class="item-label">{{ row.label }}</span>
								</div>
							</template>
						</el-table-column>
						<el-table-column v-for="(roleName, roleCode) in roles" :key="roleCode"
							:label="roleName" align="center" min-width="110">
							<template slot-scope="{ row }">
								<el-switch v-if="roleCode !== 'ADMIN'"
									:value="isMenuChecked(roleCode, row.code)"
									@change="toggleMenu(roleCode, row.code, $event)"
									:disabled="saving"
									active-color="#10b981" inactive-color="#ddd" />
								<el-tag v-else size="mini" type="success">
									<i class="el-icon-check"></i> 固有
								</el-tag>
							</template>
						</el-table-column>
					</el-table>
				</el-tab-pane>
			</el-tabs>
		</div>

		<!-- 说明 -->
		<div class="tips-card">
			<h3><i class="el-icon-info"></i> 使用说明</h3>
			<ul>
				<li><strong>ADMIN（系统管理员）</strong>：拥有所有权限和菜单，不可修改</li>
				<li><strong>功能权限</strong>：控制后端 API 的访问，修改后立即生效</li>
				<li><strong>菜单配置</strong>：控制前端侧边栏显隐，用户需重新登录生效</li>
				<li><strong>安全机制</strong>：即使前端菜单被隐藏，后端仍会校验权限，确保安全</li>
			</ul>
		</div>
	</div>
</template>

<script>
import service from '@/request/request.js';
import { MENU_CONFIG } from '@/config/menu.js';
import PageHeader from '@/components/PageHeader.vue';

/**
 * 权限分组定义 — 按菜单聚合权限
 * viewPerm 是该组的"查看"主开关; 关闭后子权限自动禁用
 */
const PERM_GROUPS = [
	{ key: 'projects', label: '项目管理', icon: 'el-icon-folder-opened', viewPerm: 'PROJECT_VIEW',
	  permissions: ['PROJECT_VIEW', 'PROJECT_CREATE', 'PROJECT_MANAGE',
	                'SPONSOR_MANAGE', 'DOCTOR_MANAGE'] },
	{ key: 'myMeetings', label: '会议管理', icon: 'el-icon-date', viewPerm: null,
	  permissions: ['MEETING_CREATE', 'MEETING_MANAGE', 'MEETING_END'] },
	{ key: 'spendingManage', label: '结算单管理', icon: 'el-icon-document-checked', viewPerm: 'SPENDING_VIEW_OWN',
	  permissions: ['SPENDING_VIEW_OWN', 'SPENDING_VIEW_ALL', 'SPENDING_CREATE', 'SPENDING_SUBMIT', 'SPENDING_EDIT',
	                'SPENDING_MATERIAL_UPLOAD', 'SPENDING_MATERIAL_OVERRIDE',
	                'SPENDING_AI_TRIGGER', 'SPENDING_AI_RETRY'] },
	{ key: 'settlementCenter', label: '审核中心', icon: 'el-icon-money', viewPerm: null,
	  permissions: ['SPENDING_AUDIT', 'SPENDING_PUSH_K3'] },
	{ key: 'userManage', label: '用户管理', icon: 'el-icon-user', viewPerm: 'USER_VIEW',
	  permissions: ['USER_VIEW', 'USER_CREATE', 'USER_EDIT', 'USER_RESET_PWD', 'USER_DISABLE'] }
];

export default {
	name: 'PermissionConfig',
	components: { PageHeader },
	data() {
		return {
			loading: false,
			saving: false,
			activeTab: 'permissions',
			roles: {},
			permissions: {},
			menus: {},
			permMatrix: {},
			menuMatrix: {},
			headerStyle: { background: '#f5f7fa', color: '#1a1a2e', fontWeight: 700 },
			// 当前选中的角色 (用于分组编辑); 默认第一个非 ADMIN 角色
			activeRole: '',
			// 默认展开"用户管理"分组, 方便快速找到刚改过的项
			expandedGroups: ['userManage']
		}
	},
	computed: {
		permGroups() {
			let groups = PERM_GROUPS.map(g => ({ ...g }));
			// 监控未分组权限 (后端新加但前端未配置): 控制台告警, 由开发处理
			const known = new Set();
			PERM_GROUPS.forEach(g => g.permissions.forEach(p => known.add(p)));
			const orphans = Object.keys(this.permissions).filter(p => !known.has(p));
			if (orphans.length > 0) {
				console.warn('[PermissionConfig] 未分组权限码 (需加到 PERM_GROUPS):', orphans);
			}
			// 过滤: 当前角色的"菜单配置"未勾的菜单, 对应权限分组也隐藏
			if (this.activeRole && this.activeRole !== 'ADMIN') {
				const enabledMenus = this.menuMatrix[this.activeRole] || [];
				groups = groups.filter(g => enabledMenus.includes(g.key));
			}
			return groups;
		},
		noMenusForRole() {
			if (!this.activeRole || this.activeRole === 'ADMIN') return false;
			const enabled = this.menuMatrix[this.activeRole] || [];
			return enabled.length === 0;
		},
		permissionRows() {
			return Object.entries(this.permissions).map(([code, label]) => ({ code, label }));
		},
		menuRows() {
			return Object.entries(this.menus).map(([code, label]) => {
				const cfg = MENU_CONFIG.find(m => m.key === code);
				return { code, label, icon: cfg ? cfg.icon : 'el-icon-menu' };
			});
		}
	},
	created() {
		this.loadMatrix();
	},
	methods: {
		async loadMatrix() {
			this.loading = true;
			try {
				const res = await service.get('/api/permission/matrix');
				if (res.code === 200) {
					this.roles = res.data.roles || {};
					this.permissions = res.data.permissions || {};
					this.menus = res.data.menus || {};
					this.permMatrix = res.data.permMatrix || {};
					this.menuMatrix = res.data.menuMatrix || {};
					// 默认选第一个非 ADMIN 角色
					if (!this.activeRole) {
						const first = Object.keys(this.roles).find(r => r !== 'ADMIN');
						if (first) this.activeRole = first;
					}
				} else {
					this.$message.error(res.msg || '加载失败');
				}
			} catch (err) {
				this.$message.error('加载系统配置失败');
			} finally {
				this.loading = false;
			}
		},

		// ===== 权限 =====
		isPermChecked(role, perm) {
			return this.permMatrix[role] && this.permMatrix[role].includes(perm);
		},

		/** 当前角色在该 group 下已启用的权限数 */
		countEnabled(group) {
			if (!this.activeRole || this.activeRole === 'ADMIN') return group.permissions.length;
			const list = this.permMatrix[this.activeRole] || [];
			return group.permissions.filter(p => list.includes(p)).length;
		},

		/**
		 * 智能切换权限:
		 * - 关闭"主开关"(viewPerm) 会级联关闭该组下所有子权限
		 * - 开启"主开关"只开自己, 不级联开其他, 让用户主动选要哪些子权限
		 */
		async togglePermSmart(group, perm, checked) {
			if (!this.activeRole) return;
			let perms = [...(this.permMatrix[this.activeRole] || [])];
			const set = new Set(perms);

			if (perm === group.viewPerm && !checked) {
				// 关闭主开关 → 同组全部移除
				group.permissions.forEach(p => set.delete(p));
			} else if (checked) {
				set.add(perm);
			} else {
				set.delete(perm);
			}
			perms = Array.from(set);
			await this.savePerms(this.activeRole, perms);
		},

		/** 真正持久化角色的权限列表 */
		async savePerms(role, perms) {
			this.saving = true;
			try {
				const res = await service.post(`/api/permission/role/${role}`, { permissions: perms });
				if (res.code === 200) {
					this.$set(this.permMatrix, role, perms);
					this.$message.success('已保存');
				} else {
					this.$message.error(res.msg || '保存失败');
				}
			} catch (err) {
				this.$message.error('保存失败');
			} finally {
				this.saving = false;
			}
		},
		async togglePerm(role, perm, checked) {
			this.saving = true;
			try {
				let perms = [...(this.permMatrix[role] || [])];
				if (checked) { if (!perms.includes(perm)) perms.push(perm); }
				else { perms = perms.filter(p => p !== perm); }

				const res = await service.post(`/api/permission/role/${role}`, { permissions: perms });
				if (res.code === 200) {
					this.$set(this.permMatrix, role, perms);
					this.$message.success(`已${checked ? '授予' : '撤销'} ${this.roles[role]} 的「${this.permissions[perm]}」权限`);
				} else {
					this.$message.error(res.msg || '操作失败');
				}
			} catch (err) {
				this.$message.error('操作失败');
			} finally {
				this.saving = false;
			}
		},

		// ===== 菜单 =====
		isMenuChecked(role, menu) {
			return this.menuMatrix[role] && this.menuMatrix[role].includes(menu);
		},
		async toggleMenu(role, menu, checked) {
			this.saving = true;
			try {
				let items = [...(this.menuMatrix[role] || [])];
				if (checked) { if (!items.includes(menu)) items.push(menu); }
				else { items = items.filter(m => m !== menu); }

				const res = await service.post(`/api/permission/menu/${role}`, { menus: items });
				if (res.code === 200) {
					this.$set(this.menuMatrix, role, items);
					this.$message.success(`已${checked ? '开启' : '关闭'} ${this.roles[role]} 的「${this.menus[menu]}」菜单`);
				} else {
					this.$message.error(res.msg || '操作失败');
				}
			} catch (err) {
				this.$message.error('操作失败');
			} finally {
				this.saving = false;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
#permissionConfig {
	padding: 32px 40px;
	background: #f0f2f8;
	min-height: 100vh;

	/* .page-header 已抽到 PageHeader 组件 */

	.config-card {
		background: #fff;
		border-radius: 14px;
		padding: 24px;
		margin-bottom: 24px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

		.tab-desc {
			font-size: 13px;
			color: #8f9bb3;
			margin-bottom: 16px;
			padding: 10px 14px;
			background: rgba(102, 126, 234, 0.04);
			border-radius: 8px;

			i { color: #667eea; margin-right: 6px; }
		}

		.item-cell {
			display: flex;
			align-items: center;
			gap: 4px;

			.item-code {
				font-size: 10px;
				font-family: 'SF Mono', Consolas, monospace;
				color: #a8a29e;
				display: none;
			}

			.item-label {
				font-size: 13px;
				font-weight: 600;
				color: #1a1a2e;
			}
		}

		/* ========== 分组权限 UI ========== */
		.perm-toolbar {
			display: flex;
			align-items: center;
			gap: 12px;
			margin-bottom: 16px;
			padding: 10px 14px;
			background: #fafbfc;
			border-radius: 6px;

			.tb-label {
				font-size: 13px;
				color: #4a5568;
				font-weight: 500;
			}
		}

		::v-deep .el-collapse {
			border-top: none;
			border-bottom: none;

			.el-collapse-item__header {
				padding: 0 12px;
				font-size: 14px;
				height: 44px;
				line-height: 44px;
				background: #fafbfc;
				border-radius: 6px;
				margin-bottom: 4px;
				border-bottom: 1px solid #eef0f6;
			}

			.el-collapse-item__wrap {
				border-bottom: none;
				background: transparent;
			}

			.el-collapse-item__content {
				padding: 8px 12px 16px;
			}
		}

		.group-header {
			display: flex;
			align-items: center;
			gap: 8px;
			width: 100%;

			> i {
				color: #667eea;
				font-size: 16px;
			}

			.group-title {
				font-weight: 600;
				color: #1a1a2e;
			}

			.group-count {
				margin-left: auto;
				margin-right: 8px;
				font-size: 12px;
				color: #8f9bb3;
				font-family: 'SF Mono', Consolas, monospace;
			}
		}

		.group-body {
			.perm-item {
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 8px 12px;
				border-bottom: 1px dashed #eef0f6;

				&:last-child { border-bottom: none; }

				.perm-meta {
					display: flex;
					flex-direction: column;
					gap: 2px;

					.item-label {
						font-size: 13px;
						font-weight: 500;
						color: #1a1a2e;
						display: inline-flex;
						align-items: center;
					}

					.item-code {
						font-size: 11px;
						color: #a8a29e;
						font-family: 'SF Mono', Consolas, monospace;
					}
				}
			}
		}
	}

	.tips-card {
		background: #fff;
		border-radius: 14px;
		padding: 20px 24px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

		h3 {
			font-size: 14px;
			font-weight: 700;
			color: #1a1a2e;
			margin: 0 0 12px 0;
			i { color: #667eea; margin-right: 6px; }
		}

		ul {
			margin: 0;
			padding-left: 20px;
			li {
				font-size: 13px;
				color: #64748b;
				line-height: 2;
			}
		}
	}
}
</style>
